// Machine credentials.
// Route: /settings/api-keys
//
// An API key's plaintext is returned by POST /api-keys and never again; only
// its bcrypt digest is stored. The reveal dialog is therefore the one and only
// chance to copy it, and it says exactly that.

import { useEffect, useState } from "react";

import { http, qs } from "../api";
import { useSession } from "../App";
import {
  Badge,
  Banner,
  Confirm,
  CopyButton,
  Dialog,
  DialogFooter,
  Empty,
  Field,
  Pager,
  Search,
  Table,
  useAsync,
  useToast,
  type Column,
} from "../ui";
import {
  PAGE_SIZE,
  errMessage,
  ErrorBanner,
  useDebounced,
  fmtDateTime,
  ActiveBadge,
} from "./shared";

export interface APIKeyRow {
  id: string;
  name: string;
  key_prefix: string;
  last_used_at: string | null;
  expires_at: string | null;
  is_active: boolean;
  created_at: string;
}

interface APIKeyListResponse {
  api_keys: APIKeyRow[];
  total: number;
  page: number;
  limit: number;
}

export interface APIKeyCreated {
  id: string;
  name: string;
  /** Plaintext. Returned by this one call and stored nowhere we can read. */
  key: string;
  key_prefix: string;
  expires_at: string | null;
  created_at: string;
}

// ============================================================ APIKeysView

export function APIKeysView() {
  const { can } = useSession();
  const toast = useToast();
  const canWrite = can("api_keys", "write");
  const canDeleteKey = can("api_keys", "delete");

  const [search, setSearch] = useState("");
  const query = useDebounced(search);
  const [page, setPage] = useState(1);
  useEffect(() => {
    setPage(1);
  }, [query]);

  const { data, error, loading, reload } = useAsync<APIKeyListResponse>(
    () => http.get<APIKeyListResponse>(`/api-keys${qs({ search: query, page, limit: PAGE_SIZE })}`),
    [query, page]
  );
  const keys = data?.api_keys ?? [];
  const total = data?.total ?? keys.length;

  const [createOpen, setCreateOpen] = useState(false);
  const [name, setName] = useState("");
  const [expiresAt, setExpiresAt] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [revealed, setRevealed] = useState<APIKeyCreated | null>(null);
  const [acknowledged, setAcknowledged] = useState(false);
  const [toDelete, setToDelete] = useState<APIKeyRow | null>(null);

  const create = async () => {
    if (!name.trim()) {
      toast.push("Give the key a name", "critical");
      return;
    }
    setSubmitting(true);
    try {
      const body: { name: string; expires_at?: string } = { name: name.trim() };
      // The input is local wall-clock; the API wants RFC3339.
      if (expiresAt) body.expires_at = new Date(expiresAt).toISOString();
      const created = await http.post<APIKeyCreated>("/api-keys", body);
      setCreateOpen(false);
      setName("");
      setExpiresAt("");
      setAcknowledged(false);
      setRevealed(created);
      reload();
    } catch (e) {
      toast.push(errMessage(e, "Could not create the key"), "critical");
    } finally {
      setSubmitting(false);
    }
  };

  const remove = async () => {
    if (!toDelete) return;
    try {
      await http.del(`/api-keys/${toDelete.id}`);
      toast.push("API key revoked", "good");
      setToDelete(null);
      reload();
    } catch (e) {
      toast.push(errMessage(e, "Could not revoke the key"), "critical");
      throw e;
    }
  };

  const expired = (key: APIKeyRow) => Boolean(key.expires_at && new Date(key.expires_at) < new Date());

  const columns: Column<APIKeyRow>[] = [
    { key: "name", header: "Name", sortable: true, render: (k) => <span className="strong">{k.name}</span> },
    {
      key: "key_prefix",
      header: "Key",
      render: (k) => <code>whm_{k.key_prefix}…</code>,
    },
    {
      key: "last_used_at",
      header: "Last used",
      sortable: true,
      render: (k) => fmtDateTime(k.last_used_at),
    },
    { key: "expires_at", header: "Expires", sortable: true, render: (k) => fmtDateTime(k.expires_at) },
    {
      key: "is_active",
      header: "Status",
      sortable: true,
      render: (k) =>
        expired(k) ? <Badge tone="critical">Expired</Badge> : <ActiveBadge active={k.is_active} />,
    },
    {
      key: "actions",
      header: "Actions",
      align: "right",
      render: (k) => (
        <button
          className="btn icon sm ghost"
          title="Revoke this key"
          aria-label={`Revoke ${k.name}`}
          disabled={!canDeleteKey}
          onClick={() => setToDelete(k)}
        >
          ✕
        </button>
      ),
    },
  ];

  return (
    <div className="page">
      <div className="page-head">
        <div>
          <h1>API keys</h1>
          <div className="sub">Credentials for machines calling this organisation's API.</div>
        </div>
        <div className="page-actions">
          <button
            className="btn primary"
            disabled={!canWrite}
            onClick={() => {
              setName("");
              setExpiresAt("");
              setCreateOpen(true);
            }}
          >
            + Create API key
          </button>
        </div>
      </div>

      <ErrorBanner error={error} what="Could not load the API keys" />

      <div className="card flush">
        <div className="card-head">
          <h3>Your API keys</h3>
          <Search value={search} onChange={setSearch} placeholder="Search keys…" />
        </div>
        <div className="card-body">
          <p className="muted" style={{ marginBottom: "var(--sp-3)" }}>
            Only the first characters of a key are stored in readable form. A key you lose cannot be
            recovered — revoke it and create another.
          </p>
          <Table
            columns={columns}
            rows={keys}
            loading={loading}
            empty={
              <Empty
                title={query ? "No matching keys" : "No API keys yet"}
                body={
                  query
                    ? `Nothing matches "${query}".`
                    : "Create a key to let a script or an integration call the API on this organisation's behalf."
                }
                icon="⚿"
                action={
                  !query && canWrite ? (
                    <button className="btn primary sm" onClick={() => setCreateOpen(true)}>
                      + Create API key
                    </button>
                  ) : undefined
                }
              />
            }
          />
        </div>
        <div className="card-foot">
          <Pager
            total={total}
            limit={PAGE_SIZE}
            offset={(page - 1) * PAGE_SIZE}
            itemName="API keys"
            onChange={(offset) => setPage(Math.floor(offset / PAGE_SIZE) + 1)}
          />
        </div>
      </div>

      <Dialog open={createOpen} onClose={() => setCreateOpen(false)} title="Create API key">
        <Banner tone="warning">
          The key is shown once, on the next screen, and never again. Have somewhere to paste it
          before you continue.
        </Banner>
        <Field label="Name" required hint="What will use this key.">
          <input
            value={name}
            placeholder="Policy sync job"
            onChange={(e) => setName(e.target.value)}
          />
        </Field>
        <Field label="Expires" hint="Leave blank for a key that never expires.">
          <input
            type="datetime-local"
            value={expiresAt}
            onChange={(e) => setExpiresAt(e.target.value)}
          />
        </Field>
        <DialogFooter>
          <button className="btn" onClick={() => setCreateOpen(false)} disabled={submitting}>
            Cancel
          </button>
          <button className="btn primary" onClick={() => void create()} disabled={submitting}>
            {submitting && <span className="spinner" />}
            Create key
          </button>
        </DialogFooter>
      </Dialog>

      {/* The one and only sighting of the plaintext. The server keeps a bcrypt
          digest, so there is no route that could show it a second time. */}
      <Dialog
        open={revealed !== null}
        onClose={() => setRevealed(null)}
        title="Copy this key now"
      >
        <Banner tone="critical">
          This is the only time this key will ever be shown. It is stored as a one-way hash — nobody,
          including support, can retrieve it later. If you lose it, revoke the key and create a new
          one.
        </Banner>
        <Field label={`Key for "${revealed?.name ?? ""}"`}>
          <input value={revealed?.key ?? ""} readOnly className="mono" onFocus={(e) => e.target.select()} />
        </Field>
        <div className="row" style={{ marginBottom: "var(--sp-4)" }}>
          <CopyButton value={revealed?.key ?? ""} label="Copy API key" />
          <span className="muted">Copy the key to your clipboard.</span>
        </div>
        <h4>Using it</h4>
        <pre className="pre-block">
          {`curl -H "X-API-Key: ${revealed?.key ?? ""}" https://your-api.com/api/contacts`}
        </pre>
        <label className="check">
          <input
            type="checkbox"
            checked={acknowledged}
            onChange={(e) => setAcknowledged(e.target.checked)}
          />
          <span>I have saved this key somewhere safe.</span>
        </label>
        <DialogFooter>
          <button className="btn primary" disabled={!acknowledged} onClick={() => setRevealed(null)}>
            Done
          </button>
        </DialogFooter>
      </Dialog>

      <Confirm
        open={toDelete !== null}
        onClose={() => setToDelete(null)}
        onConfirm={remove}
        danger
        title="Revoke API key"
        confirmLabel="Revoke key"
        body={
          <>
            Anything still calling the API with <strong className="strong">{toDelete?.name}</strong>{" "}
            starts failing immediately. This cannot be undone.
          </>
        }
      />
    </div>
  );
}
